import React, { createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { User, CurrentCompany, UserRoleType } from "@/types";
import { getRolesForCompany, isReadOnlyRoles } from "@/utils/roles";
import { getCompaniesByUserId, getUserById, logOut as doLogOut } from "@/services/Login";
import { useTranslation } from "react-i18next";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import {
  changeLocale,
  getUserContextIds,
  removeRedirection,
  removeUserContextIds,
  removeUserToken,
  setUserContextIds as setStoredUserContextIds,
} from "@/utils/sessionStorage";
import { useNavigate } from "react-router-dom";

interface AuthContextType {
  user: User | undefined;
  setUser: React.Dispatch<React.SetStateAction<User | undefined>>;
  companies: CurrentCompany[];
  setCompanies: React.Dispatch<React.SetStateAction<CurrentCompany[]>>;
  currentCompany: CurrentCompany | undefined;
  setCurrentCompany: React.Dispatch<React.SetStateAction<CurrentCompany | undefined>>;
  userContextIds: { companyId: number; userId: number } | null;
  setUserContextIds: React.Dispatch<React.SetStateAction<{ companyId: number; userId: number } | null>>;
  changeCompany: (companyId: number) => Promise<void>;
  setLocale: (user: User, timeZone?: string) => void;
  isCurrentCompanyAdmin: boolean;
  isUserAdmin: boolean;
  setUserEmail: React.Dispatch<React.SetStateAction<string | null>>;
  getPort: () => number | null;
  userEmail: string | null;
  signOut: () => void;

  // role helpers added
  hasRole: (company?: CurrentCompany, roleType?: string) => boolean;
  isAdminUser: (company?: CurrentCompany) => boolean;
  /**
   * True when the user holds roles in this company but none grant write access.
   * Named isViewer for historical reasons - a user with VIEWER *and* USER is not read-only.
   */
  isViewer: (company?: CurrentCompany) => boolean;
  isUserRole: (company?: CurrentCompany) => boolean;
  isLoading: boolean;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within a AuthProvider");
  }
  return context;
};

export const AuthProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [user, setUser] = useState<User | undefined>(undefined);
  const [currentCompany, setCurrentCompany] = useState<CurrentCompany | undefined>(undefined);
  const [companies, setCompanies] = useState<CurrentCompany[]>([]);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [userContextIds, setUserContextIds] = useState<{
    companyId: number;
    userId: number;
  } | null>(getUserContextIds());
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const navigate = useNavigate();

  const isCurrentCompanyAdmin = useMemo(() => currentCompany?.companyMembershipType === "ADMIN", [currentCompany]);

  const [userQuery, companiesQuery] = useQueries({
    queries: [
      {
        queryKey: ["user", userContextIds?.userId],
        queryFn: async () => {
          if (userContextIds?.userId == null) throw new Error("no userId");
          const res = await getUserById(userContextIds.userId as number);
          return res?.data ?? res;
        },
        enabled: !!userContextIds?.userId,
        staleTime: 1000 * 60 * 5,
      },
      {
        queryKey: ["companies", userContextIds?.userId, userContextIds?.companyId],
        queryFn: async () => {
          if (userContextIds?.userId == null || userContextIds?.companyId == null) throw new Error("missing ids");
          const res = await getCompaniesByUserId(userContextIds.userId as number, userContextIds.companyId as number);
          //TODO: comment this back in, the BE is behind and this causes problems
          // const filtered = res?.data.companies.filter((company) => company.approvedByAdmin);
          // if (filtered.length === 0) {
          //   console.log("THIS HAPPENS");
          //   throw new Error(`User ${userContextIds.userId} is not approved for company ${userContextIds.companyId}`);
          // }
          return {
            userId: userContextIds.userId,
            companies: res?.data.companies ?? [],
          };
        },
        enabled: !!userContextIds?.userId && !!userContextIds?.companyId,
        staleTime: 1000 * 60 * 5,
      },
    ],
  }) as unknown as any[];

  useEffect(() => {
    if (userQuery?.data) {
      setUser(userQuery.data);
    }
  }, [userQuery?.data]);

  useEffect(() => {
    const data = companiesQuery?.data;
    if (!data) return;
    const comps = data.companies ?? [];
    setCompanies(comps);

    if (comps.length === 0) {
      setCurrentCompany({} as CurrentCompany);
      return;
    }
    const targetCompany =
      comps.find((c: CurrentCompany) => c.companyId === userContextIds?.companyId) ||
      comps.find((c: CurrentCompany) => c.companyId === currentCompany?.companyId) ||
      comps[0];

    setCurrentCompany(targetCompany);
  }, [companiesQuery?.data, userContextIds?.companyId, currentCompany?.companyId]);

  const clearSession = useCallback(() => {
    removeRedirection();
    removeUserToken();
    removeUserContextIds();
    setCompanies([]);
    setCurrentCompany({} as CurrentCompany);
    setUser({} as User);
    setUserEmail(null);
    setUserContextIds(null);
  }, []);

  const changeCompany = useCallback(
    async (companyId: number) => {
      const newCompany = companies.find((comp) => comp.companyId === companyId);
      if (!newCompany) {
        setCurrentCompany({} as CurrentCompany);
        return;
      }
      const userId = user?.id;
      if (!userId) return;

      const newContext = { userId, companyId: newCompany.companyId };
      setCurrentCompany(newCompany);
      setUserContextIds(newContext);

      setStoredUserContextIds(newContext);

      if (user?.language?.languageCode) {
        changeLocale(user.language.languageCode, i18n, newCompany.companyTimeZone);
      }

      await queryClient.invalidateQueries();
      navigate("/dashboard");
      window.location.reload();
    },
    [companies, user, i18n, queryClient, setStoredUserContextIds]
  );

  const signOut = useCallback(async () => {
    try {
      await doLogOut();
    } catch (err) {
      console.warn("signOut: remote logout failed", err);
    }
    clearSession();
    window.location.replace("/login");
  }, [clearSession]);

  const setLocale = useCallback(
    (user: User, timeZone?: string) => {
      if (user.language && user.language.languageCode) {
        changeLocale(user.language.languageCode, i18n, timeZone);
      }
    },
    [i18n]
  );

  const getPort = useCallback(() => {
    const local = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
    if (local) {
      const port = window.location.host.split(":")[1] || "80";
      return Number(port);
    }
    return null;
  }, []);

  // role helpers (use user state from the provider)
  const rolesFor = useCallback((company?: CurrentCompany) => getRolesForCompany(user, company), [user]);

  const hasRole = useCallback(
    (company?: CurrentCompany, roleType?: string) => !!roleType && rolesFor(company).includes(roleType as UserRoleType),
    [rolesFor]
  );

  const isAdminUser = useCallback((company?: CurrentCompany) => hasRole(company, "ADMIN"), [hasRole]);
  const isUserRole = useCallback((company?: CurrentCompany) => hasRole(company, "USER"), [hasRole]);
  const isViewer = useCallback((company?: CurrentCompany) => isReadOnlyRoles(rolesFor(company)), [rolesFor]);

  // single source of truth: derived from the role array via getRolesForCompany
  const isUserAdmin = useMemo(() => isAdminUser(currentCompany), [isAdminUser, currentCompany]);

  const isLoading = userQuery.isLoading || companiesQuery.isLoading;
  // Object.keys(currentCompany).length === 0 ||
  // Object.keys(user).length === 0;

  const contextValue = useMemo<AuthContextType>(
    () => ({
      currentCompany,
      setCurrentCompany,
      user,
      setUser,
      companies,
      setCompanies,
      setLocale,
      userContextIds,
      setUserContextIds,
      isCurrentCompanyAdmin,
      isUserAdmin,
      changeCompany,
      setUserEmail,
      userEmail,
      getPort,
      signOut,
      hasRole,
      isAdminUser,
      isViewer,
      isUserRole,
      isLoading,
    }),
    [
      currentCompany,
      user,
      companies,
      setCurrentCompany,
      setUser,
      setCompanies,
      setLocale,
      isCurrentCompanyAdmin,
      isUserAdmin,
      setUserEmail,
      userEmail,
      changeCompany,
      userContextIds,
      setUserContextIds,
      getPort,
      signOut,
      hasRole,
      isAdminUser,
      isViewer,
      isUserRole,
      isLoading,
    ]
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};
